import { useEffect, useState } from "react";
import { Building2, Check, ChevronDown, Globe2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "novatus_acting_tenant_id";

/**
 * Mandanten-Wechsler für den Super-Admin.
 * - Globaler Modus (kein `X-Acting-Tenant-Id`): zeigt Daten aller Mandanten.
 * - Spezifischer Mandant: scoped alle API-Calls auf diesen Tenant.
 *
 * Wir setzen den Wert im localStorage; der Axios-Interceptor in lib/api.js
 * sendet ihn als Header `X-Acting-Tenant-Id` an das Backend.
 */
export const TenantSwitcher = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [open, setOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [acting, setActing] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  useEffect(() => {
    let mounted = true;
    // Super-Admin: /superadmin/tenants (alle Mandanten, global-Modus möglich).
    // Multi-Tenant-Direktor/Manager: /me/tenants (nur eigene Memberships).
    const endpoint = isSuperAdmin ? "/superadmin/tenants" : "/me/tenants";
    api.get(endpoint)
      .then((r) => { if (mounted) setTenants(r.data || []); })
      .catch(() => {
        if (!isSuperAdmin) return;
        // Super-Admin-Endpoint fehlgeschlagen → fallback auf eigene Memberships.
        api.get("/me/tenants")
          .then((r2) => { if (mounted) setTenants(r2.data || []); })
          .catch(() => {});
      });
    return () => { mounted = false; };
  }, [isSuperAdmin]);

  const setTenant = (tenantId) => {
    if (tenantId) {
      localStorage.setItem(STORAGE_KEY, tenantId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setActing(tenantId || "");
    setOpen(false);
    // Hard reload so every cached list/page refetches under the new scope.
    window.location.reload();
  };

  const activeTenant = tenants.find((t) => t.id === acting);
  // Für Nicht-Super-Admins: default = native/primary Tenant. „Global" ist Super-Admin-only.
  const primaryTenant = tenants.find((t) => t.is_primary) || tenants[0];
  const label = activeTenant
    ? activeTenant.name
    : (isSuperAdmin ? "Alle Mandanten" : (primaryTenant?.name || "Mandant wählen"));
  const showGlobalOption = isSuperAdmin;
  const menuTitle = isSuperAdmin ? "Super-Admin · Mandant wählen" : "Mandant wechseln";

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="tenant-switcher-button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-9 px-2 sm:px-2.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 ring-1 ring-amber-500/40 text-amber-200 text-[12px] sm:text-xs font-medium transition active:scale-95 max-w-[140px] sm:max-w-[220px]"
        title="Mandant wechseln"
      >
        {(!acting && isSuperAdmin) ? <Globe2 size={13} /> : <Building2 size={13} />}
        <span className="truncate hidden xl:inline">{label}</span>
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="tenant-switcher-menu"
            className="absolute right-0 mt-1 w-72 z-50 rounded-xl bg-[#101015] ring-1 ring-zinc-800 shadow-2xl shadow-black/60 overflow-hidden"
          >
            <div className="px-3 py-2 text-[12px] uppercase tracking-[0.16em] text-zinc-300 font-bold border-b border-zinc-800">
              {menuTitle}
            </div>
            {showGlobalOption && (
              <button
                type="button"
                data-testid="tenant-switcher-option-all"
                onClick={() => setTenant("")}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition text-left ${!acting ? "bg-amber-500/10 text-amber-200" : "hover:bg-zinc-800/60 text-zinc-200"}`}
              >
                <Globe2 size={14} className="flex-shrink-0" />
                <span className="flex-1">Alle Mandanten (global)</span>
                {!acting && <Check size={14} className="text-amber-300" />}
              </button>
            )}
            <div className="max-h-72 overflow-y-auto border-t border-zinc-800/60">
              {tenants.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`tenant-switcher-option-${t.slug || t.id}`}
                  onClick={() => setTenant(t.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition text-left ${acting === t.id ? "bg-amber-500/10 text-amber-200" : "hover:bg-zinc-800/60 text-zinc-200"}`}
                >
                  <Building2 size={14} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{t.name}</div>
                    <div className="text-[12px] text-zinc-300 font-mono truncate">{t.slug}</div>
                  </div>
                  {t.active === false && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">inaktiv</span>
                  )}
                  {acting === t.id && <Check size={14} className="text-amber-300 flex-shrink-0" />}
                </button>
              ))}
              {tenants.length === 0 && (
                <div className="px-3 py-4 text-xs text-zinc-300 text-center">Keine Mandanten gefunden.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
